import React, { useEffect, useRef, useState } from "react";
import AgoraRTC, {
  IAgoraRTCClient,
  ILocalAudioTrack,
  ILocalVideoTrack,
  IRemoteVideoTrack,
  IRemoteAudioTrack,
} from "agora-rtc-react";
import {
  agoraCheckSessionEndedOTMService,
  agoraInfoOTMService,
  agoraLiveCommentsOTMService,
  agoraRequestListingOTMService,
  agoraSessionDetailOTMService,
  agoraSessionEndOTMService,
  rejectRequestOTMService,
  rejectRequestPodcastService,
  rightsAllowOTMService,
  rightsRevokeOTMService,
} from "../services/VideoCallService";
import { useSearchParams } from "react-router-dom";
import OnetoMany from "../components/OnetoMany";
import "react-toastify/dist/ReactToastify.css";
import AppMessage from "../Constant/AppMessage";
import { AgoraAppId } from "../utils/config";
import {
  ICheckSessionEndResponse,
  IMessage,
  IMessageResponse,
  IRejectResponse,
  IRequestListResponse,
  IRequestUser,
  IRightsAllowResponse,
  IRightsRevokeResponse,
  ISessionDetailResponse,
  ISessionDetailResult,
  ISessionEndResponse,
  ISessionResponse,
  ISessionResult,
} from "../@types/VideoCallTypes";
import { getErrorDataCase } from "../utils/CommonFunction";
import VirtualBackgroundExtension from "agora-extension-virtual-background";

let intervalRefSessionDetail: ReturnType<typeof setInterval>;
let intervalRefRequestListing: ReturnType<typeof setInterval>;
let intervalRefComments: ReturnType<typeof setInterval>;

type RemoteUser = {
  uid: string;
  videoTrack?: IRemoteVideoTrack;
  audioTrack?: IRemoteAudioTrack;
  screenTrack?: IRemoteVideoTrack;
  videoEnabled: boolean;
  audioEnabled: boolean;
};

let processor: any = null;
// Whether screen sharing is active
let isScreenSharing: boolean = false;

// Whether the remote user's video is turned on
let isRemoteVideoOn: boolean = false;

// Whether the remote user's audio is turned on
let isRemoteAudioOn: boolean = false;

// Stores the screen track (if screen sharing is active)
let _screenTrack: ILocalVideoTrack | null = null;

// Unique session ID, null if no session is active
let sessionId: any = "";

// Stores the list of remote users in the call
// Each user has a unique UID and may have video, audio, and screen tracks
let remoteUsers: RemoteUser[] = [];

const OneToManyCallScreen = () => {
  // Stores the currently selected user in the call
  // "local" represents the current user, and a number represents a remote user's UID
  const [selectedUser, setSelectedUser] = useState<"local" | number>("local");

  // Manages the active tab in the UI
  // Default is "all", but it could be switched based on user interaction
  const [activeTab, setActiveTab] = useState<string>("all");

  // States for tracking microphone and camera status
  const [micOn, setMic] = useState<boolean>(true);

  // States for tracking the connection and calling status
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [calling, setCalling] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // States for tracking local media tracks (microphone, camera, screen sharing)
  const [localMicrophoneTrack, setLocalMicrophoneTrack] =
    useState<ILocalAudioTrack | null>(null);
  const [localCameraTrack, setLocalCameraTrack] =
    useState<ILocalVideoTrack | null>(null);
  const [screenTrack, setScreenTrack] = useState<ILocalVideoTrack | null>(null);

  // State for managing UI state (e.g., error messages, permissions)
  const [showPermissionError, setShowPermissionError] =
    useState<boolean>(false);

  //concent modal states
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  // State for managing errors and messages
  const [error, setError] = useState<string | null>("");
  const [msg, setMsg] = useState<string | null>("");
  const [showError, setShowError] = useState<boolean>(false);

  // State for managing student and tutor profiles
  const [celebProfile, setCelebProfile] = useState<ISessionResult | null>(null);
  const [sessionDetail, setSessionDetail] =
    useState<ISessionDetailResult | null>(null);

  const [requestList, setRequestList] = useState<IRequestUser[]>([]);

  const [commentList, setCommentList] = useState<IMessage[]>([]);

  // Reference to the Agora engine (RTC client)
  const agoraEngineRef = useRef<IAgoraRTCClient>();

  // UI and permission management
  const [hasPermissions, setHasPermissions] = useState<boolean>(false);
  const [permissionsGranted, setPermissionsGranted] = useState({
    microphone: false,
    camera: false,
  });
  const [refrsh, setRefresh] = useState<boolean>(false);

  // State for search parameters (URL search query)
  const [searchParams, setSearchParams] = useSearchParams();

  const encString: any = searchParams.get("data")
    ? searchParams.get("data")
    : "";

  // Update global screen and camera track references
  useEffect(() => {
    _screenTrack = screenTrack;
  }, [screenTrack]);

  // Check for media permissions
  useEffect(() => {
    checkPermissions().then((hasPermission) => {
      if (hasPermission) {
        setHasPermissions(true);
      }
    });
  }, []);

  // Fetch Agora user info if token and permissions are available
  useEffect(() => {
    if (hasPermissions) {
      fetchAgoraUserInfo();
    }
  }, [hasPermissions]);

  // Initialize Agora client
  useEffect(() => {
    agoraEngineRef.current = AgoraRTC.createClient({
      mode: "live",
      codec: "vp8",
      role: "host",
    });
    const extension = new VirtualBackgroundExtension();
    // Register plugin
    AgoraRTC.registerExtensions([extension]);
  }, []);

  // Set up Agora event listeners
  useEffect(() => {
    if (hasPermissions) {
      // Event listeners for Agora RTC client
      agoraEngineRef.current?.on(
        "user-joined",
        (user: any, mediaType: string) => {
          handleUserPublished(user, mediaType, true);
        }
      );

      agoraEngineRef.current?.on("user-left", handleUserUnpublished);

      // Event listener for remote user published
      agoraEngineRef.current?.on("user-published", handleUserPublished);

      agoraEngineRef.current?.on("user-unpublished", remoteUserUnpublished);

      return () => {
        // Cleanup
        agoraEngineRef.current?.removeAllListeners();
      };
    }
  }, [hasPermissions]);

  const remoteUserUnpublished = (user: any, mediaType: string) => {
    const uid = user.uid.toString();

    remoteUsers = remoteUsers.map((remoteUser) => {
      if (remoteUser.uid === uid) {
        if (mediaType === "video") {
          return {
            ...remoteUser,
            videoTrack: undefined,
            videoEnabled: false,
          }; // Clear video track
        } else if (mediaType === "audio") {
          return {
            ...remoteUser,
            audioTrack: undefined,
            audioEnabled: false,
          }; // Clear audio track
        }
      }
      return remoteUser; // Return unchanged if not the target user
    });
    setRefresh((prev) => !prev);
  };

  // Api calls

  const fetchAgoraUserInfo = async () => {
    try {
      let data = {
        enc: encString,
      };
      let response: ISessionResponse = await agoraInfoOTMService(data);
      if (response.result) {
        setMsg(AppMessage.waitMsg);
        setCelebProfile(response.result);
        global.authToken = response.result.jwtToken;
        sessionId = response.result.sessionId;
        const channel = response.result.channelName;
        const token = response.result.token;
        const tutorUid = response.result.userId;
        await new Promise((resolve) => setTimeout(resolve, 3000));
        let isEnded = await checkSessionEnded();
        if (!isEnded) {
          handleJoinChannel(tutorUid, token, channel);
          agoraLiveComments();
          intervalRefComments = setInterval(agoraLiveComments, 5000);
        } else {
          handleLeaveChannel();
        }
      }
    } catch (err) {
      setError(getErrorDataCase(err));
      console.log(AppMessage.requestFailed);
    }
  };

  const getSessionDetail = async () => {
    try {
      let data = {
        session_id: sessionId,
      };
      let response: ISessionDetailResponse = await agoraSessionDetailOTMService(
        data
      );
      if (response.result) {
        setSessionDetail(response.result);
        setRefresh((prev) => !prev);
      }
    } catch (err) {
      setError(getErrorDataCase(err));
    }
  };

  const getRequestListing = async () => {
    try {
      let data = {
        session_id: sessionId,
      };
      let response: IRequestListResponse = await agoraRequestListingOTMService(
        data
      );
      if (response.result) {
        setRequestList(response.result);
      }
    } catch (err) {
      setError(getErrorDataCase(err));
    }
  };
  const requestRightsAllow = async (userId: number) => {
    try {
      setLoading(true);

      console.log("Accepted", userId);

      let data = {
        session_id: sessionId,
        userId: userId,
      };
      let response: IRightsAllowResponse = await rightsAllowOTMService(data);
      if (response.result) {
        console.log("User granted rights successfully");

        getSessionDetail();
        getRequestListing();
      }
    } catch (err: any) {
      setError(getErrorDataCase(err));
      onPressShowError();
    } finally {
      setLoading(false);
    }
  };

  const requestRejectRequest = async (userId: number) => {
    try {
      setLoading(true);

      let data = {
        session_id: sessionId,
        userId: userId,
      };
      let response: IRejectResponse = await rejectRequestOTMService(data);
      if (response.result) {
        console.log("User granted rights successfully");
        getRequestListing();
      }
    } catch (err: any) {
      setError(getErrorDataCase(err));
      onPressShowError();
    } finally {
      setLoading(false);
    }
  };

  const requestRightsRevoke = async (userId: number) => {
    try {
      setLoading(true);
      console.log("Reject", userId);

      let data = {
        session_id: sessionId,
        userId: userId,
      };
      let response: IRightsRevokeResponse = await rightsRevokeOTMService(data);
      if (response.result) {
        console.log("User rights revoke successfully");
        getSessionDetail();
        getRequestListing();
      }
    } catch (err: any) {
      setError(getErrorDataCase(err));
      onPressShowError();
    } finally {
      setLoading(false);
    }
  };

  const checkSessionEnded = async () => {
    try {
      let data = {
        session_id: sessionId,
      };
      let response: ICheckSessionEndResponse =
        await agoraCheckSessionEndedOTMService(data);
      if (response.result === false) {
        return response.result;
      } else {
        setError(AppMessage.sessionExpire);
        setMsg("");
        return true;
      }
    } catch (err: any) {
      setMsg("");
      setError(getErrorDataCase(err));
      return true;
    }
  };

  const agoraLiveComments = async () => {
    try {
      let data = {
        session_id: sessionId,
      };
      let response: IMessageResponse = await agoraLiveCommentsOTMService(data);
      if (response.result) {
        setCommentList(response.result);
      }
    } catch (err: any) {
      setError(getErrorDataCase(err));
    }
  };
  const onPressLeaveCall = async () => {
    try {
      let data = {
        session_id: sessionId,
      };
      let response: ISessionEndResponse = await agoraSessionEndOTMService(data);
      if (response.result) {
        setMsg(AppMessage.CallEnd);
        setError("");
        handleLeaveChannel();
      }
    } catch (err: any) {
      setError(getErrorDataCase(err));
      onPressShowError();
    } finally {
      setShowConfirmModal(false);
    }
  };

  const checkPermissions = async () => {
    try {
      // Check microphone permission
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      micStream.getTracks().forEach((track) => track.stop());

      // Check camera permission
      const camStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      camStream.getTracks().forEach((track) => track.stop());

      setPermissionsGranted({ microphone: true, camera: true });
      return true;
    } catch (error) {
      console.error("Permission error:", error);
      setShowPermissionError(true);
      setPermissionsGranted({ microphone: false, camera: false });
      return false;
    }
  };

  const registerExtension = async (localCameraTrack: any) => {
    if (!processor) {
      const extension = new VirtualBackgroundExtension();
      AgoraRTC.registerExtensions([extension]);

      processor = extension.createProcessor();
      await processor.init();

      await localCameraTrack
        .pipe(processor)
        .pipe(localCameraTrack.processorDestination);
    }
  };

  const handleJoinChannel = async (
    tutorUid: number | null,
    token: string,
    channel: string
  ) => {
    console.log("handleJoinChannel", tutorUid, token, channel);
    if (hasPermissions) {
      try {
        agoraEngineRef.current?.on("connection-state-change", (state) => {
          console.log("Connection state changed:", state);
          if (state === "CONNECTED") {
            setIsConnected(true);
          } else {
            setIsConnected(false);
          }
        });
        setMsg("");
        setError("");

        // Create Local Tracks (Microphone and Camera)
        // const [micTrack, camTrack] =
        //   await AgoraRTC.createMicrophoneAndCameraTracks();

        const [micTrack, camTrack] =
          await AgoraRTC.createMicrophoneAndCameraTracks(
            {}, // micTrack ke liye config (blank rakh sakte ho)
            {
              encoderConfig: {
                width: 430, // Portrait width
                height: 1280, // Portrait height
                frameRate: 30,
                bitrateMin: 600,
                bitrateMax: 1200,
              },
              facingMode: "user", // front camera
            }
          );
        setLocalMicrophoneTrack(micTrack);
        setLocalCameraTrack(camTrack);
        await registerExtension(camTrack);
        let res = await agoraEngineRef.current?.join(
          AgoraAppId,
          channel,
          token,
          tutorUid
        );

        if (!agoraEngineRef.current?.uid) return;
        // Publish Local Tracks
        await agoraEngineRef.current?.publish([micTrack, camTrack]);

        setCalling(true);
        // Api call getSessionDetail
        getSessionDetail();
        intervalRefSessionDetail = setInterval(getSessionDetail, 10000);

        // Api call getRequestListing
        getRequestListing();
        intervalRefRequestListing = setInterval(getRequestListing, 10000);
      } catch (error) {
        setError(AppMessage.requestFailed);
        handleLeaveChannel();
        console.error("Error joining channel:", error);
      }
    } else {
      setShowPermissionError(true);
      setMsg("");
    }
  };

  const onPressMic = (val: boolean) => {
    setMic(val);
    if (localMicrophoneTrack) {
      if (val) {
        localMicrophoneTrack.setVolume(100); // Unmute
      } else {
        localMicrophoneTrack.setVolume(0); // Mute
      }
    }
  };

  const handleLeaveChannel = async () => {
    try {
      if (localMicrophoneTrack) {
        localMicrophoneTrack.stop();
        localMicrophoneTrack.close();
      }
      if (localCameraTrack) {
        localCameraTrack.stop();
        localCameraTrack.close();
      }

      if (screenTrack) {
        screenTrack.stop();
        screenTrack.close();
      }

      await agoraEngineRef.current?.leave();

      if (intervalRefSessionDetail) clearInterval(intervalRefSessionDetail);
      if (intervalRefRequestListing) clearInterval(intervalRefRequestListing);
      if (intervalRefComments) clearInterval(intervalRefComments);

      setIsConnected(false);
      setCalling(false);
      setLocalMicrophoneTrack(null);
      setLocalCameraTrack(null);
      setScreenTrack(null);
      setRequestList([]);
      setSessionDetail(null);

      isScreenSharing = false;

      remoteUsers = [];
      setCommentList([]);
      processor = null;
    } catch (error) {
      console.error("Error leaving channel:", error);
    }
  };

  const onPressCalling = (val: boolean) => {
    if (calling) {
      setShowConfirmModal(true);
    }
  };

  const onPressPermissionsError = () => {
    setShowPermissionError(!showPermissionError);
  };

  const handleUserPublished = async (
    user: any,
    mediaType: string,
    isjoin?: boolean
  ) => {
    const uid = user.uid.toString();
    console.log("handleUserPublished==>>");

    const existingUserIndex = remoteUsers.findIndex((u) => u.uid === uid);

    let updatedUser = {
      uid,
      videoTrack: remoteUsers[existingUserIndex]?.videoTrack,
      videoEnabled: remoteUsers[existingUserIndex]?.videoEnabled || false,
      audioTrack: remoteUsers[existingUserIndex]?.audioTrack,
      audioEnabled: remoteUsers[existingUserIndex]?.audioEnabled || false,
    };

    console.log("handleUserPublished==>", updatedUser);

    if (mediaType === "video" && user.hasVideo) {
      const videoElement = document.getElementById(
        `remote-video-${uid}`
      ) as HTMLVideoElement;

      if (!videoElement) {
        const newVideoElement = document.createElement("video");
        newVideoElement.id = `remote-video-${uid}`;
        newVideoElement.autoplay = true;
        newVideoElement.playsInline = true;
        document
          .getElementById("video-container")
          ?.appendChild(newVideoElement);
        updatedUser.videoEnabled = true;
      }
      await agoraEngineRef.current?.subscribe(user, "video");
      updatedUser.videoTrack = user.videoTrack as IRemoteVideoTrack;

      if (videoElement && updatedUser.videoTrack) {
        // Check if subscription was successful
        updatedUser.videoTrack.play(videoElement);
        updatedUser.videoEnabled = true;
      }
    }

    if (mediaType === "audio" && user.hasAudio) {
      await agoraEngineRef.current?.subscribe(user, "audio");
      updatedUser.audioTrack = user.audioTrack as IRemoteAudioTrack;
      if (updatedUser.audioTrack) {
        updatedUser.audioEnabled = true;
      }
      user.audioTrack?.play();
    }

    if (existingUserIndex > -1) {
      const updatedUsers = [...remoteUsers];
      updatedUsers[existingUserIndex] = updatedUser;
      remoteUsers = updatedUsers;
    } else {
      remoteUsers = [...remoteUsers, updatedUser];
    }

    setRefresh((prev) => !prev);
  };

  // Handle a remote user unpublished (e.g., leaving the call or stopping the video)
  const handleUserUnpublished = (user: any) => {
    console.log("Remote user unpublished:", remoteUsers, user.uid);
    const uid = user.uid.toString();

    remoteUsers = remoteUsers.filter((u) => u.uid !== user.uid.toString());
    setRefresh((prev) => !prev);

    const videoElement = document.getElementById(
      `remote-video-${user.uid}`
    ) as HTMLVideoElement;
    if (videoElement) {
      videoElement.srcObject = null;
    }
  };

  const onPressClose = () => {
    setShowConfirmModal(!showConfirmModal);
  };

  const onPressConfirm = () => {
    onPressLeaveCall();
  };

  const onPressSelectedUser = (val: "local" | number) => {
    setSelectedUser(val);
  };

  const onPressTabs = (val: string) => {
    setActiveTab(val);
  };

  const onPressShowError = () => {
    setShowError(!showError);
  };

  // Blur background
  const enableBlur = async () => {
    if (!processor) return;
    try {
      await processor.enable();
      processor.setOptions({
        type: "blur",
        blurDegree: 2, // Agora doc ke hisaab se 1 ya 2 valid hai
      });
    } catch (err) {
      console.error("Failed to enable blur:", err);
    }
  };

  // Image background
  const enableImage = async (imgSrc: string) => {
    if (!processor) return;
    try {
      await processor.enable();
      const imageEl = new Image();
      imageEl.src = imgSrc;
      imageEl.onload = () => {
        processor.setOptions({
          type: "img",
          source: imageEl, 
        });
      };
    } catch (err) {
      console.error("Failed to enable image background:", err);
    }
  };

  // Disable virtual background
  const disableVirtualBackground = async () => {
    if (!processor) return;
    try {
      await processor.disable();
    } catch (err) {
      console.error("Failed to disable virtual background:", err);
    }
  };

  return (
    <OnetoMany
      isConnected={isConnected}
      remoteUsers={remoteUsers}
      onPressCalling={onPressCalling}
      onPressMic={onPressMic}
      calling={calling}
      isScreenSharing={isScreenSharing}
      micOn={micOn}
      localMicrophoneTrack={localMicrophoneTrack}
      screenTrack={screenTrack}
      localCameraTrack={localCameraTrack}
      permissionsGranted={permissionsGranted}
      showPermissionError={showPermissionError}
      onPressPermissionsError={onPressPermissionsError}
      isRemoteAudioOn={isRemoteAudioOn}
      isRemoteVideoOn={isRemoteVideoOn}
      error={error}
      msg={msg}
      celebProfile={celebProfile}
      sessionDetail={sessionDetail}
      onPressClose={onPressClose}
      onPressConfirm={onPressConfirm}
      showConfirmModal={showConfirmModal}
      selectedUser={selectedUser}
      onPressSelectedUser={onPressSelectedUser}
      onPressTabs={onPressTabs}
      activeTab={activeTab}
      requestList={requestList}
      requestRightsAllow={requestRightsAllow}
      requestRightsRevoke={requestRightsRevoke}
      loading={loading}
      requestRejectRequest={requestRejectRequest}
      onPressShowError={onPressShowError}
      showError={showError}
      enableBlur={enableBlur}
      enableImage={enableImage}
      disableVirtualBackground={disableVirtualBackground}
      commentList={commentList}
    />
  );
};

export default OneToManyCallScreen;
